import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { LocationFeatureFlagSettingName } from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import { sendInvoiceReceipt } from '@frontend/api-invoices';
import { SetupFutureUsage } from '@frontend/api-weave-pay';
import { getUser } from '@frontend/auth-helpers';
import { HttpError } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { paymentsSentry, useLocationFeatureSetting } from '@frontend/payments-hooks';
import {
  PaymentsTerminalController,
  ReaderConnectErrors,
  RESTRICTED_TERMINAL_STATES,
  StoredReader,
  TerminalAppData,
  TerminalReader,
  useTerminalPaymentSession,
} from '@frontend/payments-terminal-controller';
import { shell } from '@frontend/shell-utils';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, TextLink, useAlert, useFormField } from '@frontend/design-system';
import { useInvoicePerson } from '../../../../hooks';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { useUpdatePaidRequestPoll } from '../../../hooks/use-update-paid-request-poll';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { useCreateTerminalPaymentIntent } from '../../terminals/use-create-terminal-payment-intent';
import { UseTerminalReadersQuery } from '../../terminals/use-terminal-readers-query';
import { CollectReaderPaymentDisplay } from './collect-terminal-step/collect-reader-payment-display';
import { ReaderStatusAdapter } from './collect-terminal-step/reader-status-adapter';
import { CollectTerminalStepBodyProps } from './collect-terminal.types';

const styles = {
  buttonBar: css`
    display: flex;
    justify-content: end;
    gap: ${theme.spacing(2)};
  `,
  receiptButtonBar: css`
    display: flex;
    justify-content: space-between;
  `,
};

export type CollectTerminalStepProps = CollectTerminalStepBodyProps & {
  readers: TerminalReader[];
  selectedReader: StoredReader | undefined;
  refetchReaders: UseTerminalReadersQuery['refetchReadersAsync'];
  setSelectedReader: (reader: StoredReader) => void;
};

export const CollectTerminalStep = ({
  readers,
  selectedReader,
  onGoBack,
  onInvoiceSent,
  refetchReaders,
  onPaymentSuccess,
  setSelectedReader,
  onCloseWithoutReceipt,
}: CollectTerminalStepProps) => {
  const { t } = useTranslation('payments');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const user = getUser();

  const {
    activeLocationId,
    invoice,
    activeGateway,
    invoiceQuery: { refetch: refetchInvoice },
  } = useCollectPaymentMultiContext();

  const locationId = invoice?.locationId || activeLocationId;
  const [hasConfirmedSuccess, setHasConfirmedSuccess] = useState(false);
  const [hasPollEnded, setHasPollEnded] = useState(false);

  const alert = useAlert();

  // PAYMENT INTENT
  const { person } = useInvoicePerson(invoice);

  /**
   * Should only use "offSession" when we have a "save for later"
   * checkbox and corresponding address fields, etc.
   */
  const setupFutureUsage: SetupFutureUsage | null = null;

  const onRefetchInvoice = useCallback(() => {
    return refetchInvoice().then((res) => res.data);
  }, [refetchInvoice]);

  const {
    paymentIntent,
    error: createIntentError,
    retryCreatePaymentIntent,
  } = useCreateTerminalPaymentIntent({ person, invoice, setupFutureUsage, onGoBack, onRefetchInvoice });

  const { enabled: autoReceiptEnabled } = useLocationFeatureSetting(
    LocationFeatureFlagSettingName.AUTO_RECEIPT_ENABLED,
    locationId || ''
  );

  const appData: TerminalAppData = useMemo(
    () =>
      shell.isShell
        ? {
            type: 'shell',
            version: shell.version,
          }
        : {
            type: 'nwx',
            version: undefined,
          },
    [shell]
  );

  const { setProcessingAndStartPoll } = useUpdatePaidRequestPoll(
    invoice,
    () => {
      onPaymentSuccess();
      setHasPollEnded(true);
      setHasConfirmedSuccess(true);
    },
    () => {
      setHasConfirmedSuccess(false);
      setHasPollEnded(true);
    }
  );

  const {
    error: readerError,
    status: readerStatus,
    handleCancel,
    handleTryAgain,
    paymentSuccessful,
  } = useTerminalPaymentSession({
    onPaymentSuccess: () => {
      setProcessingAndStartPoll();
    },
    reader: selectedReader,
    locationId: locationId ?? '',
    appData,
    enableServerDriven: true,
    goBack: onGoBack,
    paymentIntent: paymentIntent?.paymentIntent,
    paymentId: paymentIntent?.paymentId,
    invoiceId: invoice?.id,
    retryCreatePaymentIntent,
    discoverReaders: refetchReaders,
  });

  const handleEndActivePayment = () => {
    handleTryAgain({ selectedReader, failIfReaderInUse: false });
  };

  // TODO: implement terminal is in use
  const error = createIntentError ?? readerError;
  const isTerminalInUse = !!(error?.message === ReaderConnectErrors.alreadyInUse);

  const { personEmail } = useInvoicePerson(invoice);

  const emailReceiptFieldProps = useFormField({
    type: 'list',
    maxAllowed: 3,
  });

  const handleSendReceipt = async () => {
    setSendingReceipt(true);
    try {
      await sendInvoiceReceipt({
        invoiceId: invoice?.id as string,
        emails: emailReceiptFieldProps.value,
        userId: user?.userID,
      });
      alert.success(t('Receipt sent successfully'));
    } catch (err) {
      console.error('Error sending receipt', err);

      if (err instanceof HttpError) {
        alert.error(err.message);
        paymentsSentry.error('Failed to send invoice receipt', 'TerminalReceipt', err.message);
      } else if (err instanceof Error) {
        alert.error(t('Failed to send receipt'));
        paymentsSentry.error('Failed to send invoice receipt', 'TerminalReceipt', err.message);
      } else {
        paymentsSentry.error('Failed to send invoice receipt', 'TerminalReceipt', 'Unknown error');
      }
    }
    setSendingReceipt(false);
    onInvoiceSent();
  };

  const onSelectedReaderChange = useCallback(
    (terminal: TerminalReader) => {
      if (!locationId) return; // this should never happen, we only show readers if there is a payments url
      return setSelectedReader(PaymentsTerminalController.createStoredReader(locationId, terminal));
    },
    [locationId, setSelectedReader]
  );

  const {
    changePaymentTrackingId,
    tryAgainTrackingId,
    cancelTrackingId,
    sendReceiptTrackingId,
    closeWithoutReceiptTrackingId,
  } = useScopedTrackingIds({
    changePaymentTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--change-payment-btn`,
    tryAgainTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--try-again-btn`,
    cancelTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--cancel-btn`,
    sendReceiptTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--send-receipt-btn`,
    closeWithoutReceiptTrackingId: `${PAYMENT_MULTI_STEPS.collectTerminal}--${activeGateway}--close-without-receipt-btn`,
  });

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.collectTerminal}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.collectTerminal]}
      components={{
        Footer: () => (
          <div className='step__footer'>
            {!isTerminalInUse &&
              (!paymentSuccessful ? (
                <div css={styles.buttonBar}>
                  {readerError ? (
                    <>
                      <Button trackingId={changePaymentTrackingId} variant='secondary' size='large' onClick={onGoBack}>
                        {t('Change Payment Method')}
                      </Button>
                      <Button trackingId={tryAgainTrackingId} size='large' onClick={() => handleTryAgain()}>
                        {t('Try Again')}
                      </Button>
                    </>
                  ) : (
                    <Button trackingId={cancelTrackingId} variant='secondary' size='large' onClick={handleCancel}>
                      {t('Cancel')}
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {hasPollEnded && (
                    <div css={styles.receiptButtonBar}>
                      <TextLink trackingId={closeWithoutReceiptTrackingId} onClick={onCloseWithoutReceipt}>
                        {t('Close without receipt')}
                      </TextLink>
                      <Button
                        trackingId={sendReceiptTrackingId}
                        size='large'
                        disabled={!emailReceiptFieldProps.value.length || sendingReceipt}
                        onClick={handleSendReceipt}
                      >
                        {t('Send Receipt')}
                      </Button>
                    </div>
                  )}
                </>
              ))}
          </div>
        ),
      }}
    >
      <ContentLoader show={sendingReceipt} message={t('Sending receipt...')} />
      <CollectReaderPaymentDisplay
        selectedReader={selectedReader}
        isTerminalInUse={isTerminalInUse}
        paymentSuccessful={paymentSuccessful}
        hasConfirmedSuccess={hasConfirmedSuccess}
        hasPollEnded={hasPollEnded}
        emailReceiptFieldProps={emailReceiptFieldProps}
        ReaderStatusOverrideComponent={
          <ReaderStatusAdapter
            invoice={invoice}
            status={readerStatus}
            hasConfirmedSuccess={hasConfirmedSuccess}
            hasPollEnded={hasPollEnded}
            error={error}
            isTerminalInUse={isTerminalInUse}
            handleEndActivePayment={handleEndActivePayment}
          />
        }
        availableReaders={readers}
        onSelectedReaderChange={onSelectedReaderChange}
        disableTerminalSelection={RESTRICTED_TERMINAL_STATES.includes(readerStatus)}
        personEmail={personEmail}
        autoReceiptEnabled={!!autoReceiptEnabled}
      />
    </StyledStep>
  );
};
